import { useState, useEffect, useRef } from "react";
import Sidebar from "@/components/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Check, Crown, Zap, Building2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV !== "production" ? "http://localhost:8000" : "");
const API = `${BACKEND_URL}/api`;

const planIcons = { free: Crown, pro: Zap, enterprise: Building2 };

export default function Subscription() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [yearly, setYearly] = useState(false);
  const [payuForm, setPayuForm] = useState(null);
  const payuFormRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/subscription/plans`, { credentials: "include" });
        if (res.ok) setPlans(await res.json());
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const txnid = params.get("payu_txnid");
    if (!txnid) return;

    (async () => {
      try {
        const res = await fetch(`${API}/subscription/payu/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ txnid }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || "Payment verification failed");
          return;
        }

        const result = await res.json();
        if (result.status === "success") {
          toast.success("Payment successful. Subscription activated.");
          await checkAuth();
        } else {
          toast.error("Payment failed or cancelled.");
        }
      } catch {
        toast.error("Payment verification failed");
      } finally {
        navigate("/subscription", { replace: true });
      }
    })();
  }, [location.search, navigate, checkAuth]);

  useEffect(() => {
    if (!payuForm || !payuFormRef.current) return;
    payuFormRef.current.submit();
  }, [payuForm]);

  const handleUpgrade = (plan) => {
    setSelectedPlan(plan);
    setDialogOpen(true);
  };

  const confirmUpgrade = async () => {
    if (!selectedPlan) return;
    setActivating(true);
    try {
      const res = await fetch(`${API}/subscription/payu/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan_id: selectedPlan.plan_id, period: yearly ? "yearly" : "monthly" }),
      });
      if (res.ok) {
        const data = await res.json();
        setDialogOpen(false);
        setPayuForm(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to start payment");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-zinc-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-[#0047FF]" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50" data-testid="subscription-page">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <Toaster richColors position="top-right" />
        <div className="p-6 lg:p-8">
          <div className="mb-8 pl-12 lg:pl-0">
            <h1
              className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Subscription Plans
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Current plan: <span className="font-bold text-zinc-950 uppercase">{user?.plan || "free"}</span>
            </p>
          </div>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <span className={`text-sm font-bold ${!yearly ? "text-zinc-950" : "text-zinc-400"}`}>Monthly</span>
            <button
              data-testid="billing-toggle"
              onClick={() => setYearly(!yearly)}
              className={`relative h-7 w-12 rounded-full transition-colors ${yearly ? "bg-[#0047FF]" : "bg-zinc-200"}`}
            >
              <div className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${yearly ? "translate-x-[22px]" : "translate-x-0.5"}`} />
            </button>
            <span className={`text-sm font-bold ${yearly ? "text-zinc-950" : "text-zinc-400"}`}>
              Yearly
              <Badge className="ml-2 bg-green-100 text-green-700 border-0 text-[10px]">Save 17%</Badge>
            </span>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {plans.map((plan, i) => {
              const Icon = planIcons[plan.plan_id] || Crown;
              const isCurrent = user?.plan === plan.plan_id;
              const isPro = plan.plan_id === "pro";
              const price = yearly ? plan.price_yearly : plan.price_monthly;
              const period = yearly ? "/yr" : "/mo";

              return (
                <motion.div
                  key={plan.plan_id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.08 }}
                >
                  <Card
                    className={`border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] relative overflow-hidden transition-all duration-300 ${
                      isPro ? "border-[#0047FF] border-2 shadow-[0_8px_24px_rgba(0,71,255,0.12)]" : ""
                    }`}
                    data-testid={`plan-card-${plan.plan_id}`}
                  >
                    {isPro && (
                      <div className="absolute top-0 left-0 right-0 h-1 bg-[#0047FF]" />
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${isPro ? "bg-[#0047FF]/10" : "bg-zinc-100"}`}>
                          <Icon className={`h-4 w-4 ${isPro ? "text-[#0047FF]" : "text-zinc-600"}`} strokeWidth={1.5} />
                        </div>
                        {isPro && <Badge className="bg-[#0047FF] text-white border-0 text-[10px] font-bold">Popular</Badge>}
                      </div>
                      <CardTitle className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {plan.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-6">
                        {price === 0 ? (
                          <p className="text-3xl font-black text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                            Free
                          </p>
                        ) : (
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-black text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                              ₹{price}
                            </span>
                            <span className="text-sm text-zinc-400 font-medium">{period}</span>
                          </div>
                        )}
                      </div>
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-zinc-600">
                            <Check className="h-4 w-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      {isCurrent ? (
                        <Button
                          disabled
                          className="w-full font-bold rounded-lg"
                          variant="secondary"
                          data-testid={`current-plan-${plan.plan_id}`}
                        >
                          Current Plan
                        </Button>
                      ) : plan.plan_id === "free" ? null : (
                        <Button
                          data-testid={`upgrade-${plan.plan_id}`}
                          onClick={() => handleUpgrade(plan)}
                          className={`w-full font-bold rounded-lg active:scale-[0.98] transition-all ${
                            isPro
                              ? "bg-[#0047FF] text-white hover:bg-[#0033CC]"
                              : "bg-zinc-950 text-white hover:bg-zinc-800"
                          }`}
                        >
                          Upgrade to {plan.name}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Note about mock */}
          <p className="text-center text-xs text-zinc-400 mt-8">
            Payments are processed securely via PayU.
          </p>
        </div>
      </main>

      {payuForm?.action && payuForm?.fields && (
        <form ref={payuFormRef} action={payuForm.action} method="POST" className="hidden">
          {Object.entries(payuForm.fields).map(([k, v]) => (
            <input key={k} type="hidden" name={k} value={v ?? ""} />
          ))}
        </form>
      )}

      {/* Upgrade Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Upgrade to {selectedPlan?.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              You will be redirected to PayU to complete the payment.
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 p-4 bg-zinc-50 rounded-lg border border-zinc-200">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-zinc-500">Plan</span>
              <span className="font-bold text-zinc-950">{selectedPlan?.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Amount</span>
              <span className="font-bold text-zinc-950">
                ₹{yearly ? selectedPlan?.price_yearly : selectedPlan?.price_monthly}
                {yearly ? "/year" : "/month"}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="font-bold"
              data-testid="cancel-upgrade"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-upgrade"
              onClick={confirmUpgrade}
              disabled={activating}
              className="bg-[#0047FF] text-white hover:bg-[#0033CC] font-bold active:scale-[0.98]"
            >
              {activating ? "Processing..." : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
